/**
 * Turning an `asset.url` into something an `<img>` can actually render.
 *
 * `AssetRef` is `{ url: string }` and comes from `lib-core-types`, so an uploaded image has nowhere
 * to live but that one string — and the media bucket is PRIVATE, so what is worth storing is the
 * object path, not a link that expires within the hour. `asset.url` therefore holds either:
 *
 *   - an external URL (`https://…`, `data:…`), rendered as-is; or
 *   - an object path (`assessment_templates/12/{uuid}.png`), exchanged here for a short-lived
 *     signed link through the media endpoint.
 *
 * They are told apart by the absence of a URL scheme, which is why a stored path must never be
 * written with a leading slash.
 *
 * THE CACHE is module-level and keyed by object path: the editor field and the preview resolve the
 * same image at once, and per-component signing would mean two requests for one thumbnail. Entries
 * carry the server's own expiry and are re-fetched a minute before it.
 *
 * KNOWN GAP (§8): only app-admin resolves paths today — app-student and app-tutor read `asset.url`
 * directly, so an authored image is admin-visible before it is kid-visible.
 */
import { computed, ref, watch } from 'vue'

import type { ComputedRef, Ref } from 'vue'
import type { AssessmentMedia } from './interfaces'

import dayjs from 'dayjs'

import { useAssessmentTemplateMediaLinkApi } from '../../api/assessment-templates/media-link.hook'
import { useAssessmentEditorStore } from './store'
import { isAssessmentMediaPath } from '../../lib/assessments/assessment-media'

/** Re-sign this long before the server's expiry, so a link never dies while it is on screen. */
const REFRESH_MARGIN_MS = 60 * 1000

/** Used when `expires_at` is unparseable — short enough to be harmless, long enough to be useful. */
const FALLBACK_TTL_MS = 5 * 60 * 1000

interface CachedLink {
  url: string
  expiresAtMs: number
}

const linkCache = new Map<string, CachedLink>()

/** Contract §3, now shared with the store (which maintains the manifest and cannot import from
 *  here without a cycle). Re-exported so this module stays the address the contract names. */
export { isAssessmentMediaPath }

const expiryToMs = (expiresAt: string): number => {
  const parsed = dayjs(expiresAt)

  return parsed.isValid() ? parsed.valueOf() : Date.now() + FALLBACK_TTL_MS
}

const isFresh = (cached: CachedLink): boolean => cached.expiresAtMs - REFRESH_MARGIN_MS > Date.now()

/**
 * Seeds the cache from an upload response, so the field that just uploaded a file renders it from
 * the link it already has instead of asking the server to sign the same object again.
 */
export const rememberAssessmentMediaLink = (media: AssessmentMedia): void => {
  linkCache.set(media.path, { url: media.url, expiresAtMs: expiryToMs(media.expires_at) })
}

/** Test seam — the cache is module state and would otherwise leak between cases. */
export const clearAssessmentMediaLinkCache = (): void => {
  linkCache.clear()
}

export interface UseAssessmentMediaSrcReturn {
  /** What to put in `src`. Empty while there is nothing to show, or while a path is resolving. */
  refSrc: ComputedRef<string>
  /** The value is an uploaded object, not a pasted URL — worth showing differently. */
  refIsUploaded: ComputedRef<boolean>
  refIsResolving: Ref<boolean>
  /** Set when a path could not be signed: deleted object, or one this template does not own. */
  refError: Ref<string | null>
}

export const useAssessmentMediaSrc = (value: () => string | undefined): UseAssessmentMediaSrcReturn => {
  const store = useAssessmentEditorStore()

  const refValue = computed<string>(() => {
    const raw = value()

    return typeof raw === 'string' ? raw.trim() : ''
  })

  const refIsUploaded = computed<boolean>(() => isAssessmentMediaPath(refValue.value))

  const resolvedUrl = ref<string>('')
  const refIsResolving = ref<boolean>(false)
  const refError = ref<string | null>(null)

  const refSrc = computed<string>(() => (refIsUploaded.value ? resolvedUrl.value : refValue.value))

  /** Monotonic token: an author who replaces an image twice quickly must end up with the second
   *  resolution, whichever request happens to land last. */
  let requestSeq = 0

  const resolve = async (): Promise<void> => {
    const path = refValue.value
    const templateId = store.getTemplateId
    const token = ++requestSeq

    refError.value = null

    if (!isAssessmentMediaPath(path)) {
      resolvedUrl.value = ''
      refIsResolving.value = false
      return
    }

    // No template id means the editor has not loaded a row yet; the same watcher fires again the
    // moment it does, so this is a wait, not a failure.
    if (!templateId) {
      resolvedUrl.value = ''
      return
    }

    const cached = linkCache.get(path)

    if (cached && isFresh(cached)) {
      resolvedUrl.value = cached.url
      refIsResolving.value = false
      return
    }

    refIsResolving.value = true

    const media = await useAssessmentTemplateMediaLinkApi({
      templateId,
      path,
      onError: (message) => {
        if (token === requestSeq) refError.value = message
      },
    })

    if (token !== requestSeq) return

    refIsResolving.value = false

    if (!media) {
      resolvedUrl.value = ''
      refError.value ??= 'This file could not be loaded.'
      return
    }

    rememberAssessmentMediaLink(media)
    resolvedUrl.value = media.url
  }

  watch(
    [refValue, () => store.getTemplateId],
    () => {
      void resolve()
    },
    { immediate: true },
  )

  return {
    refSrc,
    refIsUploaded,
    refIsResolving,
    refError,
  }
}
